import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Badge, Typography, Drawer, Button } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  PercentageOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  MonitorOutlined,
  ControlOutlined,
  BankOutlined,
  MenuOutlined
  // TrophyOutlined 已移除，不再使用
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Header, Content } = Layout;
const { Text } = Typography;

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, canViewAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 檢測螢幕大小
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const handleLogout = async () => {
    setLoading(true);
    await logout();
    setLoading(false);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '個人資料',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '登出',
      onClick: handleLogout
    }
  ];

  const navigationItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '儀表板'
    },
    {
      key: '/members',
      icon: <UserOutlined />,
      label: '會員管理'
    },
    {
      key: '/agents',
      icon: <TeamOutlined />,
      label: '代理管理'
      // 已移除層級限制 - 支持無限層級下開
    },
    {
      key: '/reports',
      icon: <FileTextOutlined />,
      label: '報表中心',
      children: [
        {
          key: '/reports/agent-win-loss',
          label: '代理輸贏'
        },
        {
          key: '/reports/betting-records',
          label: '投注記錄'
        }
      ]
    }
  ];

  // 管理員專用選單
  if (canViewAdmin()) {
    navigationItems.push({
      key: 'admin',
      icon: <ControlOutlined />,
      label: '系統管理',
      children: [
        {
          key: '/admin/monitor',
          icon: <MonitorOutlined />,
          label: '系統監控'
        },
        {
          key: '/admin/odds',
          icon: <PercentageOutlined />,
          label: '賠率管理'
        },
        {
          key: '/admin/limits',
          icon: <SettingOutlined />,
          label: '限額設定'
        }
        // 業績排行選單項目已移除
      ]
    });
  }

  const getLevelName = (level) => {
    const levelNames = {
      0: '管理員',
      1: '一級代理',
      2: '二級代理',
      3: '三級代理',
      4: '四級代理',
      5: '五級代理'
    };
    return levelNames[level] || `Level ${level}`;
  };

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // 移動端用戶信息簡化版本
  const MobileUserInfo = () => (
    <Space size="small">
      <Badge 
        count={user?.creditLimit - user?.usedCredit} 
        overflowCount={99999}
        style={{ backgroundColor: '#52c41a' }}
      >
        <DollarOutlined style={{ fontSize: '16px' }} />
      </Badge>
      
      <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
        <Avatar size="small" icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
      </Dropdown>
    </Space>
  );

  // 桌面端用戶信息完整版本
  const DesktopUserInfo = () => (
    <Space size="large">
      <Badge 
        count={user?.creditLimit - user?.usedCredit} 
        overflowCount={999999}
        style={{ backgroundColor: '#52c41a' }}
      >
        <DollarOutlined style={{ fontSize: '20px' }} />
      </Badge>
      
      <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
        <Space style={{ cursor: 'pointer' }}>
          <Avatar size="small" icon={<UserOutlined />} />
          <div style={{ lineHeight: 1.2 }}>
            <Text strong>{user?.displayName || user?.username}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {getLevelName(user?.level)}
            </Text>
          </div>
        </Space>
      </Dropdown>
    </Space>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: isMobile ? '0 16px' : '0 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0
        }}>
          {/* 移動端漢堡選單按鈕（移到最左邊） */}
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{ marginRight: 12 }}
            />
          )}

          <Typography.Title
            level={isMobile ? 5 : 4}
            style={{
              margin: 0,
              marginRight: isMobile ? 16 : 40,
              whiteSpace: 'nowrap'
            }}
          >
            {isMobile ? '代理管理系統' : '代理管理系統'}
          </Typography.Title>

          {/* 桌面端導航選單 */}
          {!isMobile && (
            <Menu
              mode="horizontal"
              selectedKeys={[location.pathname]}
              items={navigationItems}
              onClick={handleMenuClick}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                backgroundColor: 'transparent'
              }}
            />
          )}
        </div>
        
        {/* 用戶信息區域 */}
        {isMobile ? <MobileUserInfo /> : <DesktopUserInfo />}
      </Header>

      {/* 移動端側邊抽屜選單 */}
      <Drawer
        title="選單"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        styles={{
          body: { padding: 0 }
        }}
        width={280}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={navigationItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
        />
        
        {/* 移動端用戶詳細信息 */}
        <div style={{ 
          padding: '16px', 
          borderTop: '1px solid #f0f0f0',
          marginTop: 'auto'
        }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size="small" icon={<UserOutlined />} />
              <div>
                <Text strong>{user?.displayName || user?.username}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {getLevelName(user?.level)}
                </Text>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DollarOutlined />
              <Text>
                可用額度: {(user?.creditLimit - user?.usedCredit)?.toLocaleString()}
              </Text>
            </div>
          </Space>
        </div>
      </Drawer>
      
      <Content style={{ 
        margin: isMobile ? '16px' : '24px', 
        minHeight: 280 
      }}>
        <Outlet />
      </Content>
    </Layout>
  );
};

export default MainLayout;